// ink-component
import React from 'react';
import styled from 'styled-components';

import HStack from '../HStack/HStack';
import Icon from '../Icon/Icon';

import { elevationTokens } from '../../tokens/globals/tokens';
import { environmentOutlinerTokens } from '../../tokens/components/EnvironmentOutliner/tokens';
import { buttonSizes, resetBrowserButton } from '../Button/styles';

export interface EnvironmentOutlinerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Callback for the click event, not available for the `production` preset */
  onClick?: React.HTMLAttributes<HTMLButtonElement>['onClick'];
  /** Defines label, color, background color, and behavior */
  preset: 'production' | 'ink' | 'impersonate';
  /** For the "impersonate" preset, defines the name of the user being impersonated */
  user?: string;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const OutlinerTag = styled.button`
  ${resetBrowserButton};
  ${buttonSizes.auto};
  align-items: center;
  bottom: 0;
  cursor: pointer;
  display: inline-flex;
  left: 50%;
  pointer-events: auto;
  position: fixed;
  transform: translateX(-50%);
  z-index: 1;
`;

const StyledOutliner = styled.div`
  bottom: 0;
  box-sizing: border-box;
  left: 0;
  outline-offset: -${environmentOutlinerTokens.CoreBorderWidth};
  outline-style: ${environmentOutlinerTokens.CoreBorderStyle};
  outline-width: ${environmentOutlinerTokens.CoreBorderWidth};
  pointer-events: none;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${elevationTokens.GlobalEnvironment};

  &::after {
    ${buttonSizes.auto};
    align-items: center;
    bottom: 0;
    display: inline-flex;
    left: 50%;
    position: fixed;
    transform: translateX(-50%);
  }
`;

export const ProductionOutliner = styled(StyledOutliner)`
  outline-color: ${environmentOutlinerTokens.ProductionColor};

  &::after {
    background-color: ${environmentOutlinerTokens.ProductionColor};
    color: ${environmentOutlinerTokens.ProductionButtonTextColor};
    content: 'Production';
  }
`;

export const ImpersonateOutliner = styled(StyledOutliner)`
  outline-color: ${environmentOutlinerTokens.ImpersonateColor};

  ${OutlinerTag} {
    background-color: ${environmentOutlinerTokens.ImpersonateColor};
    color: ${environmentOutlinerTokens.ImpersonateButtonTextColor};
  }
`;

export const InkDyeOutliner = styled(StyledOutliner)`
  outline-color: ${environmentOutlinerTokens.InkDyeColor};

  ${OutlinerTag} {
    background-color: ${environmentOutlinerTokens.InkDyeColor};
    color: ${environmentOutlinerTokens.InkDyeButtonTextColor};
  }
`;

/**
 * @purpose Outlines an environment
 * @categories accessory
 * @staffonly true
 */
export function EnvironmentOutliner(props: EnvironmentOutlinerProps) {
  const { style, className, preset, user, onClick, ...otherProps } = props;

  switch (preset) {
    case 'production':
      return <ProductionOutliner data-testid={props['data-testid']} {...otherProps} />;

    case 'ink':
      return (
        <InkDyeOutliner data-testid={props['data-testid']} {...otherProps}>
          <OutlinerTag onClick={onClick}>
            <HStack spacing="small" alignY="center">
              <Icon type="close" meaning="Deactivate ink debug mode" placement="low" />
              ink debug mode
            </HStack>
          </OutlinerTag>
        </InkDyeOutliner>
      );

    case 'impersonate':
      return (
        <ImpersonateOutliner data-testid={props['data-testid']} {...otherProps}>
          <OutlinerTag onClick={onClick}>
            <HStack spacing="small" alignY="center">
              <Icon type="close" meaning="Stop impersonating" placement="low" />
              Impersonating {user}
            </HStack>
          </OutlinerTag>
        </ImpersonateOutliner>
      );

    default:
      throw new Error('Prop `preset` is required for EnvironmentOutliner');
  }
}

EnvironmentOutliner.displayName = 'EnvironmentOutliner';

export default EnvironmentOutliner;
